import React, { useState, useEffect } from 'react';
import { 
  Lock, 
  BarChart3, 
  Activity, 
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  DollarSign,
  Users,
  TrendingUp,
  RefreshCw,
  Eye,
  EyeOff,
  LogOut,
  Zap,
  Calendar,
  Image,
  FileText,
  Sparkles,
  Crown
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboardData, setDashboardData] = useState(null);
  const [cronJobs, setCronJobs] = useState(null);
  const [webhooks, setWebhooks] = useState(null);
  const [errors, setErrors] = useState(null);
  const [health, setHealth] = useState(null);
  const [activity, setActivity] = useState(null);

  // Check if already authenticated
  useEffect(() => {
    const savedToken = localStorage.getItem('admin_token');
    if (savedToken) {
      verifyToken(savedToken);
    }
  }, []);

  const verifyToken = async (token) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/verify`, {
        headers: { 'X-Admin-Key': token }
      });
      const data = await res.json();
      if (data.valid) {
        setIsAuthenticated(true);
        loadAllData(token);
      }
    } catch (e) {
      localStorage.removeItem('admin_token');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });

      const data = await res.json();

      if (data.success) {
        localStorage.setItem('admin_token', data.token);
        setIsAuthenticated(true);
        loadAllData(data.token);
      } else {
        setError('Senha incorreta');
      }
    } catch (e) {
      setError('Erro ao conectar');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setIsAuthenticated(false);
    setDashboardData(null);
  };

  const getToken = () => localStorage.getItem('admin_token');

  const loadAllData = async (token) => {
    const headers = { 'X-Admin-Key': token || getToken() };

    try {
      const [dashRes, cronRes, webhookRes, errorRes, healthRes, activityRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/dashboard`, { headers }),
        fetch(`${API_URL}/api/admin/monitoring/cron-jobs`, { headers }),
        fetch(`${API_URL}/api/admin/monitoring/webhooks?limit=20`, { headers }),
        fetch(`${API_URL}/api/admin/monitoring/errors`, { headers }),
        fetch(`${API_URL}/api/admin/monitoring/health`, { headers }),
        fetch(`${API_URL}/api/admin/activity/recent`, { headers })
      ]);

      setDashboardData(await dashRes.json());
      setCronJobs(await cronRes.json());
      setWebhooks(await webhookRes.json());
      setErrors(await errorRes.json());
      setHealth(await healthRes.json());
      setActivity(await activityRes.json());
    } catch (e) {
      console.error('Error loading data:', e);
    }
  };

  const productIcons = {
    orcamento: FileText,
    agenda: Calendar,
    meutrampo: Image,
    clienteja: Users,
    postaja: Sparkles,
    pro: Crown
  };

  const productColors = {
    orcamento: 'from-blue-500 to-blue-600',
    agenda: 'from-green-500 to-green-600',
    meutrampo: 'from-orange-500 to-orange-600',
    clienteja: 'from-purple-500 to-purple-600',
    postaja: 'from-pink-500 to-pink-600',
    pro: 'from-amber-500 to-amber-600'
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Lock size={32} className="text-white" />
              </div>
              <h1 className="text-2xl font-bold text-white">Admin AtivaJá</h1>
              <p className="text-slate-400 mt-2">Digite a senha master para acessar</p>
            </div>

            <form onSubmit={handleLogin}>
              <div className="relative mb-4">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Senha Master"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:border-blue-500"
                  data-testid="admin-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>

              {error && (
                <p className="text-red-400 text-sm mb-4 text-center">{error}</p>
              )}

              <Button
                type="submit"
                disabled={loading || !password}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white py-3"
                data-testid="admin-login-btn"
              >
                {loading ? 'Verificando...' : 'Acessar Dashboard'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Dashboard Screen
  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Zap size={20} className="text-white" />
            </div>
            <div>
              <h1 className="font-bold text-slate-900">Admin AtivaJá</h1>
              <p className="text-xs text-slate-500">Painel Administrativo</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              onClick={() => loadAllData()}
              variant="outline"
              size="sm"
              className="gap-2"
            >
              <RefreshCw size={16} />
              Atualizar
            </Button>
            <Button
              onClick={handleLogout}
              variant="ghost"
              size="sm"
              className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <LogOut size={16} />
              Sair
            </Button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-4">
          <div className="flex gap-1">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
              { id: 'monitoring', label: 'Monitoramento', icon: Activity },
              { id: 'errors', label: 'Erros', icon: AlertTriangle }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                <tab.icon size={18} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-6">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && dashboardData && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <Users size={20} className="text-green-600" />
                  </div>
                  <span className="text-slate-500">Assinantes Ativos</span>
                </div>
                <p className="text-3xl font-bold text-slate-900">
                  {dashboardData.summary?.total_active_subscribers || 0}
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <DollarSign size={20} className="text-blue-600" />
                  </div>
                  <span className="text-slate-500">Receita Mensal</span>
                </div>
                <p className="text-3xl font-bold text-slate-900">
                  R$ {dashboardData.summary?.total_monthly_revenue?.toFixed(2) || '0.00'}
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <TrendingUp size={20} className="text-purple-600" />
                  </div>
                  <span className="text-slate-500">Novos (7 dias)</span>
                </div>
                <p className="text-3xl font-bold text-slate-900">
                  {dashboardData.summary?.total_recent_signups || 0}
                </p>
              </div>
            </div>

            {/* Products Grid */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-4 border-b border-slate-200">
                <h2 className="font-semibold text-slate-900">Vendas por Produto</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Produto</th>
                      <th className="text-center px-4 py-3 text-sm font-medium text-slate-600">Ativos</th>
                      <th className="text-center px-4 py-3 text-sm font-medium text-slate-600">Total</th>
                      <th className="text-center px-4 py-3 text-sm font-medium text-slate-600">Novos (7d)</th>
                      <th className="text-right px-4 py-3 text-sm font-medium text-slate-600">Receita/mês</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {dashboardData.products?.map((product) => {
                      const Icon = productIcons[product.product_id] || Zap;
                      const color = productColors[product.product_id] || 'from-slate-500 to-slate-600';
                      
                      return (
                        <tr key={product.product_id} className="hover:bg-slate-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 bg-gradient-to-br ${color} rounded-lg flex items-center justify-center`}>
                                <Icon size={16} className="text-white" />
                              </div>
                              <span className="font-medium text-slate-900">{product.product_name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800">
                              {product.active_subscribers}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center text-slate-600">
                            {product.total_subscribers}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {product.recent_signups > 0 ? (
                              <span className="inline-flex items-center gap-1 text-green-600">
                                <TrendingUp size={14} />
                                +{product.recent_signups}
                              </span>
                            ) : (
                              <span className="text-slate-400">0</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right font-medium text-slate-900">
                            R$ {product.monthly_revenue.toFixed(2)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Recent Activity */}
            {activity && (
              <div className="bg-white rounded-xl border border-slate-200">
                <div className="p-4 border-b border-slate-200">
                  <h2 className="font-semibold text-slate-900">Atividade Recente</h2>
                </div>
                <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
                  {activity.activities?.slice(0, 15).map((item, i) => (
                    <div key={i} className="px-4 py-3 flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${
                        item.type === 'payment' ? 'bg-green-500' :
                        item.type.includes('new') ? 'bg-blue-500' : 'bg-slate-400'
                      }`} />
                      <span className="text-sm text-slate-700 flex-1">{item.message}</span>
                      <span className="text-xs text-slate-400">
                        {item.timestamp ? new Date(item.timestamp).toLocaleDateString('pt-BR') : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Monitoring Tab */}
        {activeTab === 'monitoring' && (
          <div className="space-y-6">
            {/* Health Status */}
            {health && (
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <h2 className="font-semibold text-slate-900 mb-4">Saúde do Sistema</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(health.checks || {}).map(([key, value]) => (
                    <div key={key} className="flex items-center gap-2">
                      {value.status === 'ok' ? (
                        <CheckCircle size={18} className="text-green-500" />
                      ) : value.status === 'warning' ? (
                        <AlertTriangle size={18} className="text-amber-500" />
                      ) : (
                        <XCircle size={18} className="text-red-500" />
                      )}
                      <span className="text-sm text-slate-700 capitalize">
                        {key.replace(/_/g, ' ')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CRON Jobs */}
            {cronJobs && (
              <div className="bg-white rounded-xl border border-slate-200">
                <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                  <h2 className="font-semibold text-slate-900">CRON Jobs</h2>
                  <div className="flex gap-2 text-sm">
                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded">
                      ✓ {cronJobs.stats?.done || 0}
                    </span>
                    <span className="px-2 py-1 bg-red-100 text-red-700 rounded">
                      ✗ {cronJobs.stats?.failed || 0}
                    </span>
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                      ⟳ {cronJobs.stats?.running || 0}
                    </span>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left px-4 py-2">Tipo</th>
                        <th className="text-left px-4 py-2">Data</th>
                        <th className="text-center px-4 py-2">Status</th>
                        <th className="text-center px-4 py-2">Processados</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {cronJobs.jobs?.map((job) => (
                        <tr key={job.job_id} className="hover:bg-slate-50">
                          <td className="px-4 py-2 font-medium">{job.type}</td>
                          <td className="px-4 py-2 text-slate-600">{job.run_date}</td>
                          <td className="px-4 py-2 text-center">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                              job.status === 'DONE' ? 'bg-green-100 text-green-700' :
                              job.status === 'FAILED' ? 'bg-red-100 text-red-700' :
                              job.status === 'RUNNING' ? 'bg-blue-100 text-blue-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {job.status}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-center text-slate-600">
                            {job.processed_count}/{job.total_estimated || '?'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Webhooks */}
            {webhooks && (
              <div className="bg-white rounded-xl border border-slate-200">
                <div className="p-4 border-b border-slate-200">
                  <h2 className="font-semibold text-slate-900">Webhooks Asaas</h2>
                </div>
                <div className="p-4">
                  <div className="flex flex-wrap gap-2 mb-4">
                    {webhooks.event_stats?.map((stat) => (
                      <span key={stat._id} className="px-3 py-1 bg-slate-100 rounded-full text-sm">
                        {stat._id}: <strong>{stat.count}</strong>
                      </span>
                    ))}
                  </div>
                  <div className="max-h-60 overflow-y-auto space-y-2">
                    {webhooks.webhooks?.slice(0, 10).map((wh, i) => (
                      <div key={i} className="flex items-center gap-3 text-sm p-2 bg-slate-50 rounded">
                        <span className="font-medium text-slate-700">{wh.event}</span>
                        <span className="text-slate-400 text-xs">
                          {wh.processed_at ? new Date(wh.processed_at).toLocaleString('pt-BR') : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Errors Tab */}
        {activeTab === 'errors' && errors && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle size={24} className="text-amber-500" />
                <h2 className="font-semibold text-slate-900">
                  {errors.total_errors || 0} Erros Encontrados
                </h2>
              </div>

              {errors.failed_jobs?.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-slate-700 mb-2">Jobs com Falha</h3>
                  <div className="space-y-2">
                    {errors.failed_jobs.map((job) => (
                      <div key={job.job_id} className="p-3 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-red-800">{job.type}</span>
                          <span className="text-xs text-red-600">{job.run_date}</span>
                        </div>
                        <p className="text-sm text-red-700">{job.last_error || 'Erro desconhecido'}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(!errors.failed_jobs || errors.failed_jobs.length === 0) && (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle size={48} className="mx-auto mb-3 text-green-500" />
                  <p>Nenhum erro encontrado!</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
